import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { signOut } from '../../features/auth/data/authRepository.js';
import { cx } from '../../shared/lib/cx.js';
import styles from './HeaderMenu.module.css';
import {
  ChecklistIcon,
  IncomeIcon,
  LogOutIcon,
  MenuIcon,
  ProfileIcon,
  ReconcileIcon,
  RepeatIcon,
  TagIcon,
} from './navIcons.js';

/**
 * Hamburger menu in the app header.
 *
 * Hand-rolled: the stack skill forbids headless component libraries. It deliberately does
 * NOT use role="menu" — that role promises arrow-key navigation between items, which this
 * does not implement. A labelled button with aria-expanded plus a plain list of links is
 * honest about what it is and works with a screen reader as-is.
 */
export function HeaderMenu({ displayName }: { displayName: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function onPointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) setIsOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div className={cx(styles.wrapper)} ref={wrapperRef}>
      <button
        type="button"
        className={cx(styles.trigger)}
        aria-label="Menu"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <MenuIcon />
      </button>

      {isOpen ? (
        <div className={cx(styles.panel)} id={panelId}>
          {/* Repeated here because the header only has room for a truncated name. */}
          <p className={cx(styles.panelName)}>{displayName}</p>

          <Link className={cx(styles.item)} to="/ho-so" onClick={() => setIsOpen(false)}>
            <ProfileIcon />
            Hồ sơ
          </Link>

          <Link className={cx(styles.item)} to="/nguon-thu" onClick={() => setIsOpen(false)}>
            <IncomeIcon />
            Nguồn thu nhập
          </Link>

          <Link className={cx(styles.item)} to="/danh-muc" onClick={() => setIsOpen(false)}>
            <TagIcon />
            Danh mục
          </Link>

          <Link className={cx(styles.item)} to="/dinh-ky" onClick={() => setIsOpen(false)}>
            <RepeatIcon />
            Khoản định kỳ
          </Link>

          <Link className={cx(styles.item)} to="/doi-soat" onClick={() => setIsOpen(false)}>
            <ReconcileIcon />
            Đối chiếu ví
          </Link>

          <Link className={cx(styles.item)} to="/viec-thang" onClick={() => setIsOpen(false)}>
            <ChecklistIcon />
            Việc hàng tháng
          </Link>

          <Link className={cx(styles.item)} to="/thiet-lap" onClick={() => setIsOpen(false)}>
            <ChecklistIcon />
            Thiết lập ban đầu
          </Link>

          <button
            type="button"
            className={cx(styles.item)}
            onClick={() => {
              setIsOpen(false);
              void signOut();
            }}
          >
            <LogOutIcon />
            Đăng xuất
          </button>
        </div>
      ) : null}
    </div>
  );
}
